import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, PlayCircle, PauseCircle, Award, Menu, CircleDot, Lock, Volume2, VolumeX, RotateCcw, ShieldAlert, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../../components/ui/sheet';
import {
  academyGetCourse, academySaveProgress, academySubmitAttempt,
  academyAcknowledge, academyAssetBlobUrl, academyOpenCertificatePdf,
  academyGetProgress, academyAudioComplete, academyInteractionComplete,
  academyOpenPreviewCertificate,
} from '../../../services/api';

/**
 * Universal Lutrom Academy course player.
 *
 * Renders any Lutrom Course Package v1.0 — all 16 screen types in one
 * switch so the renderer stays small and every new course reuses the
 * same code path.
 *
 * **Employee mode** (`preview=false`) enforces the training-integrity
 * requirements: automatic narration with an anti-seek watermark that
 * only stamps completion on genuine `ended` events, mandatory
 * interaction completion for interactive screens, sequential progress
 * (future screens locked in the TOC), and server-anchored progress so
 * refresh / re-login / other-device cannot bypass the gates.
 *
 * **Admin preview** (`preview=true`) bypasses every gate and writes
 * nothing to `academy_progress`, `academy_attempts`,
 * `academy_certificates`, or `academy_acknowledgements`. It exposes a
 * "Preview Certificate" button that hits the admin-only
 * `/academy/preview/{cid}/{ver}/certificate` endpoint — a watermarked
 * sample PDF, no cert number burned, no DB row created.
 */
export default function CoursePlayer({ preview = false }) {
  const { courseId, version } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modIdx, setModIdx] = useState(0);
  const [scrIdx, setScrIdx] = useState(0);
  // Server-anchored per-screen completion. `screensCompleted` is the
  // union set the TOC + Next gate read; `audioDone` / `interactionDone`
  // are kept separately so the "one of the two is done" state can be
  // shown to the learner and re-hydrated on refresh.
  const [audioDone, setAudioDone] = useState(new Set());
  const [interactionDone, setInteractionDone] = useState(new Set());
  const [progressHydrated, setProgressHydrated] = useState(false);
  const [phase, setPhase] = useState('learning');  // learning | assessment | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  // iOS Safari blocks autoplay until the user gestures. Track whether
  // we've unlocked; once unlocked, subsequent screens autoplay silently.
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await academyGetCourse(courseId, version);
        setCourse(c);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Course not available');
        navigate('/employee/training');
      }
    })();
  }, [courseId, version, navigate]);

  // Hydrate progress from server on load (learner mode only) so
  // previously completed screens stay unlocked after refresh /
  // re-login / device swap. Preview mode never reads or writes.
  useEffect(() => {
    if (!course || preview) { setProgressHydrated(true); return; }
    (async () => {
      try {
        const p = await academyGetProgress(courseId, version);
        setAudioDone(new Set(p.audioCompleted || []));
        setInteractionDone(new Set(p.interactionCompleted || []));
        // Restore the learner to where they left off — but never past
        // the furthest legitimate position (guaranteed since the server
        // never stamps beyond genuine completions).
        if (p.currentModuleId && p.currentScreenId && course.content?.modules) {
          for (let mi = 0; mi < course.content.modules.length; mi++) {
            const m = course.content.modules[mi];
            if (m.id !== p.currentModuleId) continue;
            for (let si = 0; si < m.screens.length; si++) {
              if (m.screens[si].id === p.currentScreenId) {
                setModIdx(mi); setScrIdx(si);
              }
            }
          }
        }
      } catch (_) { /* soft-fail — fresh start is fine */ }
      finally { setProgressHydrated(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, preview, courseId, version]);

  const modules = course?.content?.modules || [];
  const assessment = course?.content?.assessment;
  const currentMod = modules[modIdx];
  const currentScr = currentMod?.screens?.[scrIdx];

  // Flatten the module/screen tree into a single sequential index so
  // "the learner may only enter screens ≤ their furthest legitimately
  // unlocked position" reduces to a simple integer comparison.
  const flat = useMemo(() => {
    const out = [];
    modules.forEach((m, mi) => {
      m.screens.forEach((s, si) => out.push({ mi, si, screen: s, module: m }));
    });
    return out;
  }, [modules]);

  const _INTERACTIVE = ['multiple_choice', 'true_false', 'multi_select',
    'scenario', 'image_choice', 'acknowledgement', 'flip_cards', 'image_hotspots'];
  const isInteractive = (s) => s && _INTERACTIVE.includes(s.type);
  const hasAudio = (s) => !!s?.audio;
  const isScreenComplete = (s) => {
    if (!s) return false;
    if (hasAudio(s) && !audioDone.has(s.id)) return false;
    if (isInteractive(s) && !interactionDone.has(s.id)) return false;
    return true;
  };

  const screensCompleted = useMemo(() => {
    const set = new Set();
    flat.forEach(({ screen }) => {
      if (isScreenComplete(screen)) set.add(screen.id);
    });
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, audioDone, interactionDone]);

  // Sequential unlock: first index whose predecessors are all complete
  // AND itself is not yet complete — that's the current furthest slot.
  const furthestIdx = useMemo(() => {
    for (let i = 0; i < flat.length; i++) {
      if (!screensCompleted.has(flat[i].screen.id)) return i;
    }
    return Math.max(0, flat.length - 1);
  }, [flat, screensCompleted]);

  const currentIdx = flat.findIndex(f => f.mi === modIdx && f.si === scrIdx);
  const percent = flat.length
    ? Math.round((screensCompleted.size / flat.length) * 100) : 0;

  // Screen-level derived flags for the gate UI.
  const currentHasAudio = hasAudio(currentScr);
  const currentHasInteraction = isInteractive(currentScr);
  const currentAudioOk = !currentHasAudio || audioDone.has(currentScr?.id);
  const currentInteractionOk = !currentHasInteraction || interactionDone.has(currentScr?.id);
  const currentComplete = currentAudioOk && currentInteractionOk;
  const canAdvance = preview || currentComplete;

  const saveProgress = async () => {
    if (preview || !currentMod || !currentScr) return;
    try {
      await academySaveProgress(courseId, version, {
        currentModuleId: currentMod.id,
        currentScreenId: currentScr.id,
        screensCompleted: Array.from(screensCompleted),
        percent,
      });
    } catch (_) { /* soft-fail */ }
  };

  const advance = async () => {
    if (!currentScr) return;
    if (!canAdvance) return;
    if (scrIdx + 1 < currentMod.screens.length) {
      setScrIdx(scrIdx + 1);
    } else if (modIdx + 1 < modules.length) {
      setModIdx(modIdx + 1); setScrIdx(0);
    } else if (assessment && assessment.enabled) {
      setPhase('assessment');
    } else {
      setPhase('result');
      setResult({ passed: true, scorePercent: 100, noAssessment: true });
    }
    await saveProgress();
  };

  const back = () => {
    if (scrIdx > 0) setScrIdx(scrIdx - 1);
    else if (modIdx > 0) {
      const prevMod = modules[modIdx - 1];
      setModIdx(modIdx - 1);
      setScrIdx(prevMod.screens.length - 1);
    }
  };

  // Interactive-screen callback: mark interaction done. In learner mode
  // this posts to the server; in preview mode it stays purely local.
  const markInteractionDone = async (screenId) => {
    if (!screenId) return;
    if (interactionDone.has(screenId)) return;
    const next = new Set(interactionDone); next.add(screenId);
    setInteractionDone(next);
    if (preview) return;
    try { await academyInteractionComplete(courseId, version, screenId); }
    catch (_) { /* soft-fail — client state already flipped */ }
  };

  // Audio-completion callback fired by `NarrationController` only on a
  // genuine `ended` event where the watermark reached natural duration.
  const markAudioDone = async (screenId) => {
    if (!screenId) return;
    if (audioDone.has(screenId)) return;
    const next = new Set(audioDone); next.add(screenId);
    setAudioDone(next);
    if (preview) return;
    try { await academyAudioComplete(courseId, version, screenId); }
    catch (_) { /* soft-fail */ }
  };

  const jumpTo = (mi, si) => {
    const targetIdx = flat.findIndex(f => f.mi === mi && f.si === si);
    if (targetIdx < 0) return;
    // Preview: unrestricted. Learner: only allow ≤ furthest unlocked.
    if (!preview && targetIdx > furthestIdx) {
      toast.error('Locked — complete the earlier screens first');
      return;
    }
    setPhase('learning');
    setModIdx(mi); setScrIdx(si); setTocOpen(false);
  };

  const submitAssessment = async () => {
    if (preview) {
      setResult({ scorePercent: 100, passed: true, previewMode: true });
      setPhase('result'); return;
    }
    const questionsPresented = pickedQuestions.map(q => q.id);
    const shaped = Object.entries(answers).map(([qid, v]) => ({
      questionId: qid, answerIds: v.answerIds || [],
      boolAnswer: v.boolAnswer ?? null,
    }));
    try {
      const r = await academySubmitAttempt(courseId, version, {
        questionsPresented, answers: shaped,
      });
      setResult(r); setPhase('result');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit assessment');
    }
  };

  const pickedQuestions = useMemo(() => {
    if (!assessment || !assessment.enabled) return [];
    const bank = assessment.question_bank.slice();
    if (assessment.randomise_questions) bank.sort(() => Math.random() - 0.5);
    return bank.slice(0, Math.min(assessment.questions_required, bank.length));
  }, [assessment]);

  if (!course || (!preview && !progressHydrated)) {
    return <div className="p-6 text-slate-500">Loading course…</div>;
  }

  const lockText = (() => {
    if (currentHasAudio && currentHasInteraction && !currentAudioOk && !currentInteractionOk) {
      return '🔒 Complete the narration and activity to continue';
    }
    if (currentHasAudio && !currentAudioOk) {
      return '🔒 Listen to the full narration to continue';
    }
    if (currentHasInteraction && !currentInteractionOk) {
      return currentScr?.type === 'acknowledgement'
        ? '🔒 Acknowledge the statement to continue'
        : (currentScr?.type === 'flip_cards' || currentScr?.type === 'image_hotspots')
          ? '🔒 Complete the activity to continue'
          : '🔒 Answer the question to continue';
    }
    return null;
  })();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-player">
      {preview && (
        <div className="bg-amber-500 text-slate-900 text-center py-1.5 text-xs sm:text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2"
          data-testid="admin-preview-banner">
          <ShieldAlert className="h-4 w-4" />
          Admin Preview — no learner records written
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
        <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 shrink-0"
          aria-label="Back" data-testid="player-close">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <Sheet open={tocOpen} onOpenChange={setTocOpen}>
          <SheetTrigger asChild>
            <button className="text-slate-500 hover:text-slate-800 shrink-0 -ml-1"
              aria-label="Open modules menu" data-testid="player-toc-open">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto" data-testid="player-toc-panel">
            <SheetHeader>
              <SheetTitle className="text-left">{course.title}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              {modules.map((m, mi) => (
                <div key={m.id}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                    Module {mi + 1} · {m.title}
                  </div>
                  <ul className="space-y-1">
                    {m.screens.map((s, si) => {
                      const flatIdx = flat.findIndex(f => f.mi === mi && f.si === si);
                      const isCurrent = phase === 'learning' && mi === modIdx && si === scrIdx;
                      const isDone = screensCompleted.has(s.id);
                      const isLocked = !preview && flatIdx > furthestIdx;
                      return (
                        <li key={s.id}>
                          <button
                            onClick={() => jumpTo(mi, si)}
                            disabled={isLocked}
                            className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 ${
                              isCurrent ? 'bg-emerald-50 text-emerald-900 font-medium' :
                              isLocked ? 'text-slate-400 cursor-not-allowed' :
                              'hover:bg-slate-50 text-slate-700'
                            }`}
                            data-testid={`toc-jump-${s.id}`}
                          >
                            {isLocked ? (
                              <Lock className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                            ) : isDone ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            ) : (
                              <CircleDot className={`h-3.5 w-3.5 shrink-0 ${isCurrent ? 'text-emerald-600' : 'text-slate-300'}`} />
                            )}
                            <span className="truncate">{s.title || s.type}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {assessment?.enabled && (
                <div className="pt-2 border-t">
                  <button
                    onClick={() => { setPhase('assessment'); setTocOpen(false); }}
                    disabled={!preview && furthestIdx < flat.length - 1}
                    className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 ${
                      !preview && furthestIdx < flat.length - 1
                        ? 'text-slate-400 cursor-not-allowed'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                    data-testid="toc-jump-assessment"
                  >
                    <Award className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    Final assessment
                  </button>
                </div>
              )}
              {preview && (
                <div className="pt-2 border-t">
                  <button
                    type="button"
                    onClick={async () => {
                      try { await academyOpenPreviewCertificate(courseId, version); }
                      catch (e) { toast.error(e?.response?.data?.detail || 'Could not open preview certificate'); }
                    }}
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                    data-testid="preview-cert-button-toc"
                  >
                    <FileText className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    Preview Certificate
                  </button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500 truncate">{course.title} · v{course.version}{preview && ' · PREVIEW'}</div>
          <div className="h-1.5 bg-slate-100 rounded mt-1 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="text-xs tabular-nums text-slate-500 w-10 sm:w-12 text-right shrink-0" data-testid="course-percent">
          {percent}%
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 sm:p-4 pb-32">
        {phase === 'learning' && currentScr && (
          <>
            <ScreenRenderer
              key={currentScr.id}
              course={course}
              screen={currentScr}
              preview={preview}
              audioUnlocked={audioUnlocked}
              onAudioUnlock={() => setAudioUnlocked(true)}
              alreadyAudioDone={audioDone.has(currentScr.id)}
              onAudioComplete={() => markAudioDone(currentScr.id)}
              onInteractionComplete={() => markInteractionDone(currentScr.id)}
              alreadyInteractionDone={interactionDone.has(currentScr.id)}
            />
            {preview && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try { await academyOpenPreviewCertificate(courseId, version); }
                    catch (e) { toast.error(e?.response?.data?.detail || 'Could not open preview certificate'); }
                  }}
                  data-testid="preview-cert-button"
                >
                  <FileText className="h-4 w-4 mr-1.5" />
                  Preview Certificate
                </Button>
              </div>
            )}
          </>
        )}
        {phase === 'assessment' && (
          <AssessmentView
            questions={pickedQuestions}
            answers={answers}
            setAnswers={setAnswers}
            onSubmit={submitAssessment}
          />
        )}
        {phase === 'result' && (
          <ResultView result={result} passMark={assessment?.pass_mark} onExit={() => navigate('/employee/training')} />
        )}
      </div>

      {phase === 'learning' && currentScr && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-3 sm:px-4 py-3 flex flex-col gap-1">
          {lockText && !preview && (
            <div className="text-xs text-slate-600 text-center" data-testid="player-lock-text">
              {lockText}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" onClick={back} disabled={modIdx === 0 && scrIdx === 0}
              data-testid="player-back" className="shrink-0">
              <ChevronLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="text-xs text-slate-500 text-center flex-1 min-w-0 truncate">
              {modIdx + 1}/{modules.length} · {scrIdx + 1}/{currentMod?.screens?.length}
            </div>
            <Button onClick={advance} disabled={!canAdvance} data-testid="player-next" className="shrink-0">
              {!canAdvance && !preview ? <Lock className="h-4 w-4" /> : (
                <>
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4 sm:ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Authenticated asset helpers
//
// The Academy asset-stream endpoint (`/api/academy/assets/...`) requires
// a Bearer token. Native `<img>/<audio>/<video>` element requests cannot
// attach that header, so the player must fetch each asset through the
// authenticated axios client and swap in the resulting object URL. This
// hook + the `Auth*` wrappers below are the ONLY way asset URLs enter
// the DOM inside this file.
// ============================================================
function useAcademyAsset(courseId, version, path) {
  const [href, setHref] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!path) { setHref(null); setErr(false); return; }
    let cancelled = false;
    let created = null;
    setErr(false);
    (async () => {
      try {
        const objUrl = await academyAssetBlobUrl(courseId, version, path);
        if (cancelled) { URL.revokeObjectURL(objUrl); return; }
        created = objUrl;
        setHref(objUrl);
      } catch (_) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [courseId, version, path]);
  return { href, err };
}

// Invisible retry helper — remounting this component triggers a fresh
// `useAcademyAsset` fetch. Used by NarrationController's error state
// so the Retry button can re-fire the blob request without needing a
// separate refetch API on the hook.
function NarrationFetchProbe({ courseId, version, path }) {
  useAcademyAsset(courseId, version, path);
  return null;
}

function AuthImg({ courseId, version, path, alt = '', className, ...rest }) {
  const { href, err } = useAcademyAsset(courseId, version, path);
  if (err) {
    return (
      <div className={`${className || ''} bg-slate-100 text-slate-400 text-xs flex items-center justify-center min-h-[6rem]`}
        data-testid="auth-img-fallback">
        Image unavailable
      </div>
    );
  }
  if (!href) {
    // Skeleton — force a minimum height so the caller's height class or
    // aspect ratio (if any) is honoured, and if none is provided we
    // still reserve a visible slot so the surrounding layout doesn't
    // reflow when the image lands.
    return <div className={`${className || ''} bg-slate-100 animate-pulse min-h-[6rem]`} aria-hidden="true" />;
  }
  return <img alt={alt} src={href} className={className} {...rest} />;
}

function AuthAudio({ courseId, version, path, className }) {
  const { href } = useAcademyAsset(courseId, version, path);
  if (!href) return null;
  return <audio controls src={href} className={className} data-testid="screen-audio" />;
}

function AuthVideo({ courseId, version, videoPath, posterPath, className }) {
  const { href } = useAcademyAsset(courseId, version, videoPath);
  const poster = useAcademyAsset(courseId, version, posterPath);
  if (!href) {
    return <div className={`${className || ''} bg-slate-100 animate-pulse`} aria-hidden="true" />;
  }
  return <video controls src={href} poster={poster.href || undefined} className={className} data-testid="screen-video" />;
}

function AuthDocLink({ courseId, version, path, children }) {
  const { href, err } = useAcademyAsset(courseId, version, path);
  if (err) return <span className="text-slate-400 mt-3 inline-block">Document unavailable</span>;
  if (!href) return <span className="text-slate-400 mt-3 inline-block">Loading document…</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-block mt-3 text-emerald-700 underline">
      {children}
    </a>
  );
}

// ============================================================
// NarrationController — anti-seek audio player
//
// The Course Player enforces "genuine listening" for narrated screens.
// We can't trust the client to just call the audio-complete endpoint
// on `ended` — a tampered client could hit it directly. Server also
// verifies the screen actually carries `audio`, but on the client we
// still refuse to fire the completion callback unless the natural
// playback watermark reached the audio's `duration`.
//
// Watermark rule: on every `timeupdate`, if `currentTime` advanced by
// a plausibly-real delta (`0 ≤ dt ≤ 0.6s`) we advance the watermark.
// Any bigger jump — user seeked forward, dragged the scrubber to the
// end, or scripted `audio.currentTime = duration` — does NOT advance
// it. On `ended`, we compare the watermark to the duration; only a
// near-complete watermark (`>= duration - 1s`) is accepted as genuine.
//
// Seeking backward is fine (it's a legitimate replay), it never
// resets the watermark and does not enable seek-past-end bypass.
//
// iOS Safari blocks audio play() without a user gesture. When the
// player first mounts on iOS the browser rejects the auto-play
// promise; we then surface a "Play Narration" button. The first
// successful click unlocks audio for the entire session — subsequent
// screens autoplay silently.
// ============================================================
function NarrationController({
  courseId, version, path, screenId, transcript,
  preview, alreadyDone, audioUnlocked, onUnlock, onNaturalEnd,
}) {
  // `err` MUST be consumed — a failed blob fetch used to leave the
  // player in a permanent skeleton with no controls, so the learner
  // couldn't start narration and couldn't tell why. Now we surface
  // it as an actionable retry.
  const { href, err } = useAcademyAsset(courseId, version, path);
  const [retryTick, setRetryTick] = useState(0);
  const audioRef = useRef(null);
  const watermarkRef = useRef(0);
  const lastTimeRef = useRef(0);
  const durationRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [needGesture, setNeedGesture] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  // A screen already completed in a previous session should NOT force
  // the learner to re-listen. Preview mode also gets no autoplay
  // pressure — the admin is auditing, not training.
  const enforced = !preview && !alreadyDone;

  // Reset watermark AND transcript-open state when the audio source
  // changes (i.e. Next / Previous navigated to a different screen).
  // Without this reset, the previous screen's transcript would remain
  // visible under the new screen's narration controls.
  useEffect(() => {
    watermarkRef.current = 0;
    lastTimeRef.current = 0;
    durationRef.current = 0;
    setShowTranscript(false);
  }, [path, screenId]);

  // Autoplay on load. If the browser rejects (iOS Safari without a
  // prior gesture), fall back to the Play Narration button.
  //
  // iOS Safari fix: when the blob URL arrives after the audio element
  // has already mounted with `src=null`, Safari can silently reject
  // `.play()` unless we explicitly call `.load()` to re-read the src.
  // We do that unconditionally on every href change — cheap, and it
  // eliminates a whole class of "narration didn't play" reports.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !href) return;
    el.muted = muted;
    try { el.load(); } catch (_) { /* older Safari can throw */ }
    const attempt = () => {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          setPlaying(true);
          setNeedGesture(false);
        }).catch(() => {
          // Autoplay blocked — wait for the user gesture. Never break
          // the player: keep the audio element and manual controls up.
          setNeedGesture(true);
          setPlaying(false);
        });
      } else {
        setPlaying(true);
      }
    };
    if (audioUnlocked) attempt();
    else {
      // First narrated screen — show the button; the click handler
      // below both records the unlock and starts playback.
      setNeedGesture(true);
    }
  }, [href, audioUnlocked, muted]);

  // Blob fetch failed — show an actionable retry state instead of a
  // permanent skeleton. Retries force `useAcademyAsset` to re-run by
  // re-mounting the inner audio path (bumping `retryTick`).
  if (err) {
    return (
      <div
        className="mt-4 border border-rose-200 bg-rose-50 rounded-md p-3 text-sm text-rose-900"
        data-testid="narration-error"
      >
        <div className="font-medium">Narration failed to load.</div>
        <div className="text-xs mt-1 text-rose-800">
          Check your connection, then retry. Your progress on this screen is not affected.
        </div>
        <button
          type="button"
          onClick={() => setRetryTick(t => t + 1)}
          className="mt-2 inline-block bg-rose-600 text-white rounded px-3 py-1.5 text-sm font-medium"
          data-testid="narration-retry"
        >
          Retry
        </button>
        {/* Force a re-fetch by remounting the hook consumer. Cheap and
            keeps state isolated to this component. */}
        <NarrationFetchProbe key={retryTick} courseId={courseId} version={version} path={path} />
      </div>
    );
  }

  if (!href) {
    // Skeleton — reserve space so the layout doesn't jump.
    return <div className="mt-4 h-10 bg-slate-100 rounded animate-pulse" aria-hidden="true" data-testid="narration-loading" />;
  }

  const play = () => {
    const el = audioRef.current; if (!el) return;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        setPlaying(true); setNeedGesture(false); onUnlock?.();
      }).catch(() => setNeedGesture(true));
    } else {
      setPlaying(true); setNeedGesture(false); onUnlock?.();
    }
  };
  const pause = () => { audioRef.current?.pause(); setPlaying(false); };
  const replay = () => {
    const el = audioRef.current; if (!el) return;
    el.currentTime = 0;
    watermarkRef.current = 0; lastTimeRef.current = 0;
    play();
  };
  const toggleMute = () => {
    const el = audioRef.current; if (!el) return;
    el.muted = !el.muted; setMuted(el.muted);
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="narration">
      <div className="flex items-center gap-2">
        {playing ? (
          <button type="button" onClick={pause}
            aria-label="Pause narration" data-testid="narration-pause"
            className="text-slate-700 hover:text-slate-900">
            <PauseCircle className="h-6 w-6" />
          </button>
        ) : (
          <button type="button" onClick={play}
            aria-label="Play narration" data-testid="narration-play"
            className="text-emerald-700 hover:text-emerald-900">
            <PlayCircle className="h-6 w-6" />
          </button>
        )}
        <button type="button" onClick={replay}
          aria-label="Replay narration" data-testid="narration-replay"
          className="text-slate-500 hover:text-slate-800">
          <RotateCcw className="h-4 w-4" />
        </button>
        <button type="button" onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'} data-testid="narration-mute"
          className="text-slate-500 hover:text-slate-800">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
        <div className="text-xs text-slate-500 flex-1 truncate">
          Narration
          {enforced && !alreadyDone && ' — genuine playback required'}
          {alreadyDone && ' — already completed'}
        </div>
      </div>
      {needGesture && enforced && (
        <button type="button" onClick={play}
          className="mt-2 w-full bg-emerald-600 text-white rounded px-3 py-2 text-sm font-medium"
          data-testid="narration-start">
          Play Narration
        </button>
      )}
      <audio
        ref={audioRef}
        src={href}
        // In enforced mode we suppress the native controls to prevent
        // the drag-to-end bypass on iOS — our custom Play/Pause/Replay
        // above is all the learner needs. When already-completed (or
        // preview mode) we let the browser render its full scrubber so
        // review is more comfortable.
        controls={!enforced}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => { durationRef.current = e.currentTarget.duration || 0; }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          const dt = t - lastTimeRef.current;
          if (dt >= 0 && dt <= 0.6) {
            // Real forward playback tick — advance the watermark.
            if (t > watermarkRef.current) watermarkRef.current = t;
          }
          // else: forward seek OR backward seek — no watermark advance.
          lastTimeRef.current = t;
        }}
        onEnded={(e) => {
          setPlaying(false);
          const dur = e.currentTarget.duration || durationRef.current || 0;
          // Genuine completion iff the watermark reached the end.
          // 1 s tolerance covers browser rounding + a legitimate final
          // gap of silence in the source.
          if (dur > 0 && watermarkRef.current >= dur - 1) {
            onNaturalEnd?.();
          }
        }}
        className={enforced ? 'hidden' : 'w-full mt-2'}
      />
      {/* Transcript — comes verbatim from `screen.transcript` in the
          imported course package. If the field is missing/empty the
          control is disabled with a clear "Transcript unavailable"
          label so learners know there's nothing to reveal (rather than
          expanding into a blank panel). */}
      {transcript && transcript.trim() ? (
        <>
          <button
            type="button"
            onClick={() => setShowTranscript(v => !v)}
            className="mt-3 text-sm text-emerald-700 underline underline-offset-2 font-medium inline-flex items-center gap-1"
            data-testid="narration-transcript-toggle"
            aria-expanded={showTranscript}
          >
            Transcript
            <span aria-hidden="true">{showTranscript ? '▴' : '▾'}</span>
          </button>
          {showTranscript && (
            <div
              className="mt-2 bg-white border border-slate-200 rounded-md p-3 sm:p-4 text-sm sm:text-[15px] leading-relaxed text-slate-800 whitespace-pre-wrap break-words"
              data-testid="narration-transcript"
            >
              {transcript}
            </div>
          )}
        </>
      ) : (
        <div
          className="mt-3 text-xs text-slate-400 italic"
          data-testid="narration-transcript-unavailable"
        >
          Transcript unavailable
        </div>
      )}
    </div>
  );
}

// ============================================================
// ScreenRenderer — 16 renderers switched by `screen.type`
// ============================================================
function ScreenRenderer({
  course, screen, preview,
  audioUnlocked, onAudioUnlock,
  alreadyAudioDone, onAudioComplete,
  alreadyInteractionDone, onInteractionComplete,
}) {
  const cid = course.courseId, ver = course.version;
  const T = screen.title ? (
    <h2 className="text-2xl font-display font-bold text-slate-900 mb-3">{screen.title}</h2>
  ) : null;
  const A = screen.audio ? (
    <NarrationController
      courseId={cid} version={ver} path={screen.audio}
      screenId={screen.id}
      transcript={screen.transcript || ''}
      preview={preview}
      alreadyDone={alreadyAudioDone}
      audioUnlocked={audioUnlocked}
      onUnlock={onAudioUnlock}
      onNaturalEnd={onAudioComplete}
    />
  ) : null;

  switch (screen.type) {
    case 'hero':
      // Hero image runs the full card width across the top; title +
      // subtitle sit underneath. `object-cover` for photographic
      // banners (Asbestos hero, Where Asbestos Hides etc.) with a
      // capped `max-h` so it stays a banner rather than dominating the
      // screen. Aspect ratio is preserved — cover crops but never
      // stretches.
      return (
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-lg overflow-hidden">
          {screen.image && (
            <AuthImg courseId={cid} version={ver} path={screen.image}
              className="w-full max-h-[45vh] object-cover" />
          )}
          <div className="p-5 sm:p-8 space-y-2">
            <h1 className="text-2xl sm:text-4xl font-display font-bold">{screen.title}</h1>
            {screen.subtitle && <p className="text-base sm:text-lg opacity-90">{screen.subtitle}</p>}
            {A}
          </div>
        </div>
      );
    case 'content': {
      // Content diagrams/infographics carry information → default to
      // `object-contain` so nothing is cropped. On desktop, honour
      // `image_position: left | right` as a responsive two-column
      // layout; on mobile the image collapses to full width above the
      // text (or below, for `bottom`).
      const pos = screen.image_position || 'top';
      const media = screen.image ? (
        <AuthImg courseId={cid} version={ver} path={screen.image}
          className={`w-full max-h-[60vh] object-contain rounded-md bg-slate-50 ${
            pos === 'left' || pos === 'right' ? 'md:max-h-none md:h-auto' : ''
          }`} />
      ) : null;
      const body = (
        <>
          {screen.body && <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{screen.body}</p>}
          {screen.key_points?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {screen.key_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      );
      if (screen.image && (pos === 'left' || pos === 'right')) {
        return (
          <div className="bg-white border rounded-md p-4 sm:p-6">
            {T}
            <div className={`grid gap-4 md:gap-6 md:grid-cols-5 ${
              pos === 'right' ? 'md:[&>*:first-child]:order-1 md:[&>*:last-child]:order-2' : ''
            }`}>
              <div className="md:col-span-2">{media}</div>
              <div className="md:col-span-3">{body}</div>
            </div>
            {A}
          </div>
        );
      }
      return (
        <div className="bg-white border rounded-md p-4 sm:p-6">
          {T}
          {pos !== 'bottom' && media && <div className="my-4">{media}</div>}
          {body}
          {pos === 'bottom' && media && <div className="my-4">{media}</div>}
          {A}
        </div>
      );
    }
    case 'warning': {
      const tones = {
        info:     'bg-blue-50 border-blue-200 text-blue-900',
        caution:  'bg-amber-50 border-amber-200 text-amber-900',
        warning:  'bg-orange-50 border-orange-200 text-orange-900',
        critical: 'bg-rose-50 border-rose-200 text-rose-900',
      };
      return (
        <div className={`border-2 rounded-md p-6 ${tones[screen.severity] || tones.warning}`}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-6 w-6" />
            <h2 className="text-xl font-bold uppercase tracking-wide">{screen.title || screen.severity}</h2>
          </div>
          <p className="whitespace-pre-wrap">{screen.body}</p>
          {A}
        </div>
      );
    }
    case 'steps':
      return (
        <div className="bg-white border rounded-md p-6">
          {T}
          <ol className="space-y-3">
            {screen.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold shrink-0">{i + 1}</div>
                <div>
                  <div className="font-semibold text-slate-900">{s.title}</div>
                  {s.description && <div className="text-sm text-slate-600 mt-0.5">{s.description}</div>}
                </div>
              </li>
            ))}
          </ol>
          {A}
        </div>
      );
    case 'flip_cards':
      return <FlipCards screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'accordion':
      return (
        <div className="bg-white border rounded-md p-6">
          {T}
          <div className="space-y-2">
            {screen.items.map((it, i) => (
              <details key={i} className="border rounded p-3">
                <summary className="cursor-pointer font-medium text-slate-900">{it.heading}</summary>
                <p className="mt-2 text-slate-600 text-sm whitespace-pre-wrap">{it.body}</p>
              </details>
            ))}
          </div>
          {A}
        </div>
      );
    case 'image_hotspots':
      return <ImageHotspots course={course} screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'scenario':
    case 'multiple_choice':
    case 'multi_select':
      return <ChoiceQuiz course={course} screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'true_false':
      return <TrueFalse screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'image_choice':
      return <ImageChoice course={course} screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'video':
      return (
        <div className="space-y-3">
          {T}
          <AuthVideo courseId={cid} version={ver} videoPath={screen.video}
            posterPath={screen.poster} className="w-full rounded" />
          {screen.caption && <p className="text-sm text-slate-500">{screen.caption}</p>}
        </div>
      );
    case 'recap':
      return (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-md p-6">
          <h2 className="text-xl font-bold text-emerald-900 mb-3">{screen.title || 'Recap'}</h2>
          <ul className="space-y-2">
            {screen.key_points.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-emerald-900">
                <CheckCircle2 className="h-4 w-4 mt-0.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          {A}
        </div>
      );
    case 'acknowledgement':
      return <Acknowledgement course={course} screen={screen} preview={preview}
        onInteractionComplete={onInteractionComplete}
        alreadyInteractionDone={alreadyInteractionDone}
        T={T} A={A} />;
    case 'flip_cards':
      return <FlipCards screen={screen}
        onInteractionComplete={onInteractionComplete} T={T} A={A} />;
    case 'resource':
      return (
        <div className="bg-white border rounded-md p-6">
          {T}
          {screen.description && <p className="text-slate-700">{screen.description}</p>}
          {screen.external_url && (
            <a href={screen.external_url} target="_blank" rel="noopener noreferrer"
              className="inline-block mt-3 text-emerald-700 underline">
              {screen.external_url}
            </a>
          )}
          {screen.document && (
            <AuthDocLink courseId={course.courseId} version={course.version} path={screen.document}>
              Open document
            </AuthDocLink>
          )}
        </div>
      );
    default:
      return <div className="text-rose-600">Unsupported screen type: {screen.type}</div>;
  }
}

function FlipCard({ card, onFlip }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <button onClick={() => { setFlipped(!flipped); if (!flipped) onFlip?.(); }}
      className="border rounded-md p-6 min-h-32 bg-white hover:bg-slate-50 text-left transition-colors"
      data-testid="flip-card">
      <div className="text-sm text-slate-500 mb-1">{flipped ? 'Back' : 'Front — tap to flip'}</div>
      <div className="text-slate-900 font-medium">{flipped ? card.back : card.front}</div>
    </button>
  );
}

function FlipCards({ screen, onInteractionComplete, T, A }) {
  const [flipped, setFlipped] = useState(new Set());
  useEffect(() => {
    if (flipped.size >= screen.cards.length) onInteractionComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped]);
  return (
    <div className="space-y-3">
      {T}
      <div className="grid gap-3 sm:grid-cols-2">
        {screen.cards.map((c, i) => (
          <FlipCard key={i} card={c}
            onFlip={() => setFlipped(prev => new Set(prev).add(i))} />
        ))}
      </div>
      {A}
    </div>
  );
}

function ChoiceQuiz({ course, screen, onInteractionComplete, T, A }) {
  const [picked, setPicked] = useState([]);
  const isMulti = screen.type === 'multi_select';
  const submitted = picked.length > 0 && !isMulti;
  // Multi-select: completion fires on Confirm (below). Single answer:
  // any pick submits the answer and satisfies the requirement.
  useEffect(() => {
    if (!isMulti && picked.length > 0) onInteractionComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);
  const [multiConfirmed, setMultiConfirmed] = useState(false);
  return (
    <div className="bg-white border rounded-md p-4 sm:p-6 space-y-3">
      {T}
      {/* Scenario media — full-width, contain so diagrams stay legible. */}
      {screen.type === 'scenario' && screen.image && (
        <AuthImg courseId={course.courseId} version={course.version} path={screen.image}
          className="w-full max-h-[50vh] object-contain rounded-md bg-slate-50" />
      )}
      {screen.scenario && <p className="text-slate-700">{screen.scenario}</p>}
      <p className="font-medium text-slate-900">{screen.question}</p>
      <div className="space-y-2">
        {screen.answers.map((a) => {
          const on = picked.includes(a.id);
          const show = submitted || multiConfirmed;
          const cls = show
            ? a.correct
              ? 'border-emerald-400 bg-emerald-50'
              : on ? 'border-rose-400 bg-rose-50' : 'border-slate-200'
            : on ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50';
          return (
            <button key={a.id}
              onClick={() => {
                if (submitted || multiConfirmed) return;
                if (isMulti) setPicked(on ? picked.filter(x => x !== a.id) : [...picked, a.id]);
                else setPicked([a.id]);
              }}
              className={`w-full text-left border rounded-md px-3 py-2 transition-colors ${cls}`}>
              {a.text}
              {show && a.feedback && (
                <div className="text-xs text-slate-600 mt-1">{a.feedback}</div>
              )}
            </button>
          );
        })}
      </div>
      {isMulti && !multiConfirmed && (
        <Button size="sm" disabled={picked.length === 0}
          onClick={() => { setMultiConfirmed(true); onInteractionComplete?.(); }}
          data-testid="multi-select-confirm">
          Confirm selection
        </Button>
      )}
      {A}
    </div>
  );
}

function TrueFalse({ screen, onInteractionComplete, T, A }) {
  const [ans, setAns] = useState(null);
  const submitted = ans !== null;
  useEffect(() => {
    if (ans !== null) onInteractionComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ans]);
  return (
    <div className="bg-white border rounded-md p-6">
      {T}
      <p className="text-slate-800 font-medium mb-4">{screen.statement}</p>
      <div className="flex gap-3">
        {[true, false].map((v) => (
          <button key={String(v)} onClick={() => setAns(v)}
            className={`flex-1 border rounded-md px-4 py-2 ${
              submitted
                ? v === screen.correct_answer ? 'border-emerald-400 bg-emerald-50'
                : v === ans ? 'border-rose-400 bg-rose-50'
                : 'border-slate-200'
                : ans === v ? 'border-emerald-500' : 'border-slate-200 hover:bg-slate-50'
            }`}>
            {v ? 'True' : 'False'}
          </button>
        ))}
      </div>
      {submitted && (
        <p className="text-sm text-slate-600 mt-3">
          {ans === screen.correct_answer ? screen.correct_feedback : screen.incorrect_feedback}
        </p>
      )}
      {A}
    </div>
  );
}

function ImageChoice({ course, screen, onInteractionComplete, T, A }) {
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    if (picked !== null) onInteractionComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);
  return (
    <div className="bg-white border rounded-md p-4 sm:p-6">
      {T}
      <p className="font-medium text-slate-900 mb-3">{screen.question}</p>
      {/* Two columns on ≥sm, single column on portrait phones so each
          option has real width and can carry a diagram/photo. `contain`
          so diagram text and details aren't cropped. */}
      <div className="grid gap-3 sm:grid-cols-2">
        {screen.options.map((o) => {
          const on = picked === o.id;
          const show = picked !== null;
          const cls = show
            ? o.correct ? 'ring-2 ring-emerald-500' : on ? 'ring-2 ring-rose-500' : 'opacity-70'
            : on ? 'ring-2 ring-emerald-500' : 'hover:opacity-90';
          return (
            <button key={o.id} onClick={() => setPicked(o.id)}
              className={`rounded-md overflow-hidden border ${cls} bg-white`}>
              <AuthImg courseId={course.courseId} version={course.version} path={o.image}
                className="w-full aspect-[4/3] object-contain bg-slate-50" />
              {o.label && <div className="p-2 text-sm text-slate-700">{o.label}</div>}
              {show && o.feedback && <div className="p-2 text-xs text-slate-600">{o.feedback}</div>}
            </button>
          );
        })}
      </div>
      {A}
    </div>
  );
}

function ImageHotspots({ course, screen, onInteractionComplete, T, A }) {
  const [opened, setOpened] = useState(new Set());
  const [pop, setPop] = useState(null);
  const allOpen = !screen.require_all || opened.size >= screen.hotspots.length;
  useEffect(() => {
    // Interaction is complete once every hotspot has been opened at
    // least once (or if the course allows partial exploration, when
    // any single hotspot has been opened).
    if (allOpen) onInteractionComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOpen]);
  return (
    // Use the full card padding budget on mobile so the tappable image
    // gets maximum width. Hotspots are positioned in % so they scale
    // with the image on any viewport.
    <div className="bg-white border rounded-md p-3 sm:p-6">
      {T}
      <div className="relative w-full">
        <AuthImg courseId={course.courseId} version={course.version} path={screen.image}
          className="w-full h-auto object-contain rounded-md bg-slate-50" />
        {screen.hotspots.map((h, i) => (
          <button key={i}
            onClick={() => { setPop({ ...h }); setOpened(new Set([...opened, i])); }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold shadow-md ${
              opened.has(i) ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'
            }`}
            style={{ left: `${h.x}%`, top: `${h.y}%` }}
            data-testid={`hotspot-${i}`}>
            {opened.has(i) ? '✓' : i + 1}
          </button>
        ))}
      </div>
      {pop && (
        <div className="mt-3 p-4 bg-slate-50 border rounded">
          <div className="font-medium">{pop.title}</div>
          <p className="text-sm text-slate-700 mt-1">{pop.description}</p>
          <Button size="sm" variant="ghost" onClick={() => setPop(null)} className="mt-2">Close</Button>
        </div>
      )}
      {!allOpen && <p className="text-xs text-slate-500 mt-3">Open every hotspot before continuing.</p>}
      {A}
    </div>
  );
}

function Acknowledgement({ course, screen, preview, onInteractionComplete, alreadyInteractionDone, T, A }) {
  const [signature, setSignature] = useState('');
  // If the learner already acknowledged this screen in a previous
  // session, hydrate the local `signed` flag from the server-side
  // interactionCompleted set so the button reads "Acknowledged ✓" and
  // the input is disabled — Next unlocks immediately without forcing a
  // second signature.
  const [signed, setSigned] = useState(!!alreadyInteractionDone);
  useEffect(() => { if (alreadyInteractionDone) setSigned(true); },
    [alreadyInteractionDone]);
  return (
    <div className="bg-white border rounded-md p-6">
      {T}
      <p className="text-slate-800 mb-4">{screen.statement}</p>
      {signed ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-3 py-2 text-sm"
          data-testid="ack-already-done">
          Acknowledged ✓ — you can continue to the next screen.
        </div>
      ) : (
        <>
          <label className="text-sm text-slate-600 block mb-1">Type your full name to acknowledge:</label>
          <input type="text" value={signature} onChange={(e) => setSignature(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
            data-testid="ack-input" />
          <Button className="mt-3" onClick={async () => {
            if (!signature.trim()) return;
            if (!preview) {
              // Convert typed-name to a tiny PNG so the server accepts it as
              // a "signature" without needing a full pad on every ack screen.
              const canvas = document.createElement('canvas');
              canvas.width = 400; canvas.height = 100;
              const ctx = canvas.getContext('2d');
              ctx.font = '32px cursive'; ctx.fillStyle = '#0f172a';
              ctx.fillText(signature, 10, 60);
              const dataUrl = canvas.toDataURL('image/png');
              try {
                await academyAcknowledge(course.courseId, course.version, {
                  screenId: screen.id, statement: screen.statement, signature: dataUrl,
                });
              } catch (_) {/* soft-fail */}
            }
            setSigned(true);
            // Flip the parent's `interactionDone` set — this is the
            // ONLY signal the outer progression gate reads. Without
            // this call, Next stayed locked even though the ack was
            // persisted server-side (regression fixed Feb 2026).
            onInteractionComplete?.();
          }} disabled={!signature.trim()} data-testid="ack-submit">
            Acknowledge
          </Button>
        </>
      )}
      {/* Render the narration widget INSIDE the ack card so a screen
          that requires audio can actually play it. Previously omitted
          — meant an ack screen with `screen.audio` could never satisfy
          the audioComplete half of the gate, keeping Next locked
          forever. */}
      {A}
    </div>
  );
}

function AssessmentView({ questions, answers, setAnswers, onSubmit }) {
  return (
    <div className="space-y-4" data-testid="assessment-view">
      <h2 className="text-2xl font-display font-bold">Final assessment</h2>
      {questions.map((q, i) => (
        <div key={q.id} className="bg-white border rounded p-4">
          <div className="text-xs text-slate-500 mb-1">Question {i + 1}</div>
          <p className="font-medium">{q.question || q.statement}</p>
          {q.type === 'true_false' ? (
            <div className="flex gap-2 mt-2">
              {[true, false].map((v) => (
                <button key={String(v)}
                  onClick={() => setAnswers({ ...answers, [q.id]: { boolAnswer: v } })}
                  className={`px-4 py-1 border rounded ${
                    answers[q.id]?.boolAnswer === v ? 'bg-emerald-600 text-white' : ''
                  }`}>{v ? 'True' : 'False'}</button>
              ))}
            </div>
          ) : (
            <div className="mt-2 space-y-1">
              {(q.answers || []).map((a) => {
                const cur = answers[q.id]?.answerIds || [];
                const on = cur.includes(a.id);
                const multi = q.type === 'multi_select';
                return (
                  <button key={a.id}
                    onClick={() => {
                      const next = multi
                        ? on ? cur.filter(x => x !== a.id) : [...cur, a.id]
                        : [a.id];
                      setAnswers({ ...answers, [q.id]: { answerIds: next } });
                    }}
                    className={`w-full text-left px-3 py-1.5 border rounded ${
                      on ? 'bg-emerald-50 border-emerald-400' : ''
                    }`}>
                    {a.text}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <Button className="w-full" onClick={onSubmit} data-testid="assessment-submit">
        Submit assessment
      </Button>
    </div>
  );
}

function ResultView({ result, passMark, onExit }) {
  if (!result) return null;
  const passed = result.passed;
  return (
    <div className={`rounded-lg p-6 ${passed ? 'bg-emerald-50 border-2 border-emerald-300' : 'bg-rose-50 border-2 border-rose-300'}`}
      data-testid="result-view">
      <Award className={`h-10 w-10 mx-auto ${passed ? 'text-emerald-600' : 'text-rose-600'}`} />
      <h2 className="text-2xl font-bold text-center mt-2">
        {passed ? 'Passed' : 'Not yet passed'}
      </h2>
      {result.scorePercent !== undefined && (
        <p className="text-center text-slate-700 mt-1">
          Score: <b>{result.scorePercent}%</b>{passMark ? ` · Pass mark: ${passMark}%` : ''}
        </p>
      )}
      {result.certificate?.certNumber && (
        <button
          type="button"
          onClick={async () => {
            try {
              await academyOpenCertificatePdf(result.certificate.certNumber);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Could not open certificate');
            }
          }}
          className="block w-full text-center mt-4 text-emerald-700 underline"
          data-testid="result-cert-link">
          View certificate ({result.certificate.certNumber})
        </button>
      )}
      <Button className="w-full mt-4" onClick={onExit}>Back to My Training</Button>
    </div>
  );
}
